import { useState } from "react";
import { Bell, MessageSquare, ClipboardList, Loader2 } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "sonner";

/**
 * One-time push opt-in priming modal. A single primary CTA triggers the native
 * browser permission prompt (required to start from a user gesture). Closing or
 * "Later" persists a server-side dismissal so it never reappears — only the
 * lightweight banner does.
 */
export function PushOptInModal({ open, onEnable, onDismiss }) {
	const { language } = useLanguage();
	const [busy, setBusy] = useState(false);

	const handleEnable = async () => {
		if (busy) return;
		setBusy(true);
		try {
			await onEnable();
			toast.success(
				language === "it"
					? "Notifiche push attivate"
					: "Push notifications enabled",
			);
		} catch (e) {
			toast.error(
				e?.message ||
					(language === "it"
						? "Impossibile attivare le notifiche push"
						: "Could not enable push notifications"),
			);
			// Convert to the reminder banner so we don't keep showing the modal.
			await onDismiss?.();
		} finally {
			setBusy(false);
		}
	};

	const handleLater = async () => {
		if (busy) return;
		setBusy(true);
		try {
			await onDismiss?.();
		} finally {
			setBusy(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) handleLater();
			}}
		>
			<DialogContent className="bg-card border-border rounded-sm sm:max-w-md">
				<DialogHeader>
					<div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-sm bg-volt/10 border border-volt/30">
						<Bell className="h-7 w-7 text-volt" />
					</div>
					<DialogTitle className="font-heading text-2xl text-center uppercase tracking-tight">
						{language === "it"
							? "Attiva le notifiche"
							: "Turn on notifications"}
					</DialogTitle>
					<DialogDescription className="text-center">
						{language === "it"
							? "Resta sempre aggiornato in tempo reale, senza dover aprire l'app."
							: "Stay updated in real time without opening the app."}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 py-2">
					<div className="flex items-start gap-3">
						<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-secondary">
							<ClipboardList className="h-4 w-4 text-volt" />
						</div>
						<p className="text-sm text-muted-foreground">
							{language === "it"
								? "Sai subito quando il coach pubblica un nuovo programma o una nuova settimana."
								: "Know instantly when your coach publishes a new program or week."}
						</p>
					</div>
					<div className="flex items-start gap-3">
						<div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-secondary">
							<MessageSquare className="h-4 w-4 text-volt" />
						</div>
						<p className="text-sm text-muted-foreground">
							{language === "it"
								? "Ricevi un avviso per ogni nuovo messaggio."
								: "Get notified for every new message."}
						</p>
					</div>
				</div>

				<div className="flex flex-col gap-2 pt-1">
					<Button
						onClick={handleEnable}
						disabled={busy}
						className="w-full rounded-sm bg-volt text-black font-bold hover:bg-volt/90"
					>
						{busy ? (
							<Loader2 className="h-4 w-4 animate-spin" />
						) : language === "it" ? (
							"Attiva ora"
						) : (
							"Enable now"
						)}
					</Button>
					<Button
						variant="ghost"
						onClick={handleLater}
						disabled={busy}
						className="w-full rounded-sm text-muted-foreground hover:text-foreground"
					>
						{language === "it" ? "Più tardi" : "Maybe later"}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
